import VehicleActions from 'common/redux/actions/vehicleMaster';

import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import { MasterVehicle } from 'common/models/internal/MasterVehicle';

import { LDate } from 'common/models/internal/LDate';

import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { vehicle as vehicleApi } from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import DataUtils from 'common/utils/DataUtils';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';

import VehicleModelIdInputWithFleetTransporter from './VehicleModelIdInputWithFleetTransporter';

import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { EntityDocumentUploadList } from 'common/entity/documents';
import { YearPicker } from 'common/components/core/form/YearPicker';

import { VehicleModelId } from 'common/models/internal/VehicleModelId';
import moment from 'moment-timezone';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { getClientDimensionsMapping } from 'common/redux/selectors/clientSelector';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

import { ENTITY_NAME_ID } from 'common/constants/entity/vehicleMaster';

export const shiftIdInputFilters = ({ entity, input }) => {
    let filters = new FilterListBuilder().addAll([
        new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValue('ACTIVE')
    ]);
    if (entity.teams && entity.teams.size > 0) {
        filters = filters.add(
            new FilterBuilder()
                .withName('teamId.teamId')
                .withOperation(FilterOperation.Equals)
                .withValues(entity.teams.toArray().map(team => team.teamId))
                .withCaseSensitive(true)
        );
    }
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const vehicleModelIdInputFilters = ({ input }) => {
    let filters = new FilterListBuilder().addAll([
        new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValue('ACTIVE')
    ]);
    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const VEHICLE_FIELDS = [
    {
        accessor: 'code',
        label: 'vehicleForm.code',
        helpText: 'vehicleForm.codeTooltip',
        placeholder: 'vehicleForm.codePlaceholder',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'vehicleForm.name',
        placeholder: 'vehicleForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'registrationNumber',
        label: 'vehicleForm.registrationNumber',
        placeholder: 'vehicleForm.registrationNumber',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'teams',
        label: 'vehicleForm.teams',
        placeholder: 'vehicleForm.teamsPlaceHolder',
        type: FieldTypeEnum.Teams,
        required: true,
        helpText: 'vehicleForm.teamsTooltip'
    },
    {
        accessor: 'vehicleModelId',
        label: 'vehicleForm.vehicleModelId',
        placeholder: 'vehicleForm.vehicleModelIdPlaceholder',
        tooltip: 'vehicleForm.vehicleModelIdTooltip',
        helpText: 'vehicleForm.vehicleModelId',
        required: true,
        component: VehicleModelIdInputWithFleetTransporter,
        extraProps: ({ entity, field, t, props }) => {
            const { entitySettings } = props;
            const customResources = CustomColumnHelpers.getCustomResources(
                entitySettings,
                'vehicleModelSettings'
            );
            const { unitConverter } = props;
            return {
                vehicleModel: entity.get('vehicleModel'),
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage,
                vehicleModelFilters: input =>
                    vehicleModelIdInputFilters({ entity, input }),
                t,
                unitConverter,
                customResources
            };
        },
        onChangeHandler: ({ entity, value: vehicleModel }) => {
            let vehicleModelId = null;
            if (vehicleModel) {
                vehicleModelId = VehicleModelId.fromJS({
                    clientId: entity.clientId,
                    modelId: vehicleModel.id,
                    version: vehicleModel.version
                });
            }
            return entity
                .setVehicleModel(vehicleModel)
                .setVehicleModelId(vehicleModelId);
        }
    },
    {
        accessor: 'isActive',
        required: true,
        label: 'vehicleForm.isActive',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t }) => {
            return {
                label: field.value
                    ? t('vehicleForm.boolean.yes')
                    : t('vehicleForm.boolean.no')
            };
        }
    },
    {
        accessor: 'manufacturingDate',
        label: 'vehicleForm.manufacturingYear',
        component: YearPicker,
        halfWidth: true,
        placeholder: 'vehicleForm.manufacturingYearPlaceholder',
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(
                field.absoluteAccessor,
                LDate.fromJS({ year: value })
            );
        },
        extraProps: ({ field, t }) => {
            return {
                value: field.value?.year
                    ? moment(field.value?.year, 'YYYY')
                    : null,
                placeholder: t(field.placeholder),
                error: field.validationMessage
            };
        }
    },
    {
        accessor: 'mileage',
        label: 'vehicleForm.mileage',
        placeholder: 'vehicleForm.mileagePlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'baseLocations.startLocation',
        label: 'vehicleForm.startLocation',
        placeholder: 'vehicleForm.startLocationPlaceholder',
        type: FieldTypeEnum.PlacesSearch,
        halfWidth: true,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        }
    },
    {
        accessor: 'baseLocations.endLocation',
        label: 'vehicleForm.endLocation',
        placeholder: 'vehicleForm.endLocationPlaceholder',
        type: FieldTypeEnum.PlacesSearch,
        halfWidth: true,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'vehicleSettings'
            };
        }
    },
    {
        accessor: 'documents',
        component: EntityDocumentUploadList,
        label: () => '',
        extraProps: ({ field, entity, t }) => {
            return {
                currentEntitySettings: 'vehicleSettings',
                documents: entity.getIn(field.absoluteAccessor),
                t
            };
        },
        onChangeHandler: ({ field, entity, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        }
    }
];

const VEHICLE_INITIAL_FETCH_FIELDS = ['skills'];

const SingleVehicleEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterVehicle,
    MasterActions: VehicleActions,
    EntityFetchAPI: vehicleApi,
    INITIAL_FETCH_FIELDS: VEHICLE_INITIAL_FETCH_FIELDS,
    FIELDS: VEHICLE_FIELDS,
    extraApiParams: {
        include: [IncludeTypeEnums.Shifts, IncludeTypeEnums.VehicleModel]
    },
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'vehicleId',
        ENTITY_SETTINGS_NAME: 'vehicleSettings'
    },
    mapEntityStateToProps: ({ state, props }) => {
        const teams = teamsSelector(state, props);
        const dimensionsMapping = getClientDimensionsMapping(state, props);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            teams,
            unitConverter
        };
    }
});

export default SingleVehicleEditContainer;
